import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import Col from "react-bootstrap/Col";
import Controls from "../components/Controls";
import CustomPolyline from "../components/CustomPolyline";
import BusMarker from "../components/BusMarker";
import BusStopMarker from "../components/BusStopMarker";
import PickedUpChildrenMarker from "../components/PickedUpChildrenMarker";
import SelectedLocationDetails from "../components/SelectedLocationDetails";
import SelectedBusDetails from "../components/SelectedBusDetails";
import useAppSelector from "../hooks/useAppSelector";
import useAppDispatch from "../hooks/useAppDispatch";
import useRestrictUrlAccess from "../hooks/useRestrictUrlAccess";
import {
  getBusAttendanceChildren,
  setSelectedBus,
  setSelectedLocation,
} from "../redux/module/busAttendances";
import {
  getBusAttendanceLocations,
  getLatestLocation,
} from "../redux/module/locations";
import { isSameLocation, distanceBetween } from "../services/location.service";
import { keyBy } from "../utils";
import { GOOGLE_MAPS_API_KEY } from "../constants";

import type { ChildData, DetailedLocation, LatLng, Location } from "../types";

const center = [
  {
    lat: 40.8236528,
    lng: -74.1250567,
  },
  {
    lat: 40.6955502,
    lng: -73.9445643,
  },
];

const getKeyFromLatLng = (l: Location) => `${l.lat}${l.lng}`;

const LocationTracking = () => {
  useRestrictUrlAccess();
  const dispatch = useAppDispatch();
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [showMapChildren, setShowMapChildren] = useState(false);
  const locations = useAppSelector((state) => state.locations.locations);
  const latestLocations = useAppSelector(
    (state) => state.locations.latestLocations
  );
  const busAttendances = useAppSelector(
    (state) => state.busAttendances.busAttendances
  );
  const selectedBusAttendance = useAppSelector(
    (state) => state.busAttendances.selectedBusAttendance
  );
  const selectedBus = useAppSelector(
    (state) => state.busAttendances.selectedBus
  );
  const selectedBusAttendanceChildren = useAppSelector(
    (state) => state.busAttendances.selectedBusAttendanceChildren
  );
  const showBusStops = useAppSelector((state) => state.controls.showBusStops);
  const showPickedUp = useAppSelector((state) => state.controls.showPickedUp);
  const stopTime = useAppSelector((state) => state.controls.stopTime);
  const accuracy = useAppSelector((state) => state.controls.accuracy);
  const selectedLocation = useAppSelector(
    (state) => state.busAttendances.selectedLocation
  );

  useEffect(() => {
    if (selectedBusAttendance) {
      dispatch(
        getBusAttendanceLocations(selectedBusAttendance.busAttendanceId)
      );
      dispatch(getBusAttendanceChildren(selectedBusAttendance.busAttendanceId));
    }
  }, [dispatch, selectedBusAttendance]);

  useEffect(() => {
    dispatch(
      getLatestLocation(busAttendances.map((item) => item.busAttendanceId))
    );
  }, [dispatch, busAttendances]);

  const roundedLocations = useMemo(() => {
    const sortedByTime = [...locations].sort((a, b) =>
      a.timestamp > b.timestamp ? 1 : -1
    );
    const childrenByLocation: Record<string, Array<ChildData>> = {};
    const rawLocations = sortedByTime.reduce((acc, item, currentIndex) => {
      const uniqueLocations = Object.values(acc).flat();
      const locationChildren = selectedBusAttendanceChildren.filter((child) =>
        isSameLocation(
          {
            lat: Number(child.location.latitude),
            lng: Number(child.location.longitude),
          },
          item
        )
      );
      if (item.accuracy && item.accuracy > accuracy) {
        return acc;
      }
      const isPrevSameLocation = isSameLocation(
        item,
        sortedByTime[currentIndex - 1]
      );
      const prevKey = uniqueLocations.find(
        (i) => i.index === currentIndex - 1
      )?.key;
      if (isPrevSameLocation && prevKey) {
        acc[prevKey].push({ ...item, key: prevKey, index: currentIndex });
        childrenByLocation[prevKey].push(...locationChildren);
      } else {
        const key = getKeyFromLatLng(item);
        acc[key] = [{ ...item, key, index: currentIndex }];
        childrenByLocation[key] = locationChildren;
      }

      return acc;
    }, {} as Record<string, Array<Location & { key: string; index: number }>>);

    return Object.entries(rawLocations).map(([key, locationPoints], index) => {
      const firstPoint = locationPoints[0];
      const lastPoint = locationPoints[locationPoints.length - 1];

      const children = Object.values(
        keyBy(childrenByLocation[key] || [], (child) => child.id)
      );
      const locationItem: DetailedLocation = {
        ...locationPoints[Math.floor(locationPoints.length / 2)],
        startedAt: firstPoint.timestamp,
        endedAt: lastPoint.timestamp,
        isStop: false,
        index,
        children,
      };

      const stoppedTime = lastPoint.timestamp - firstPoint.timestamp;
      if (stoppedTime > stopTime * 1000) {
        locationItem.isStop = true;
      }

      return locationItem;
    });
  }, [locations, stopTime, selectedBusAttendanceChildren]);

  const polylines = useMemo(() => {
    const result: Array<Array<DetailedLocation>> = [];
    const polyline: Array<DetailedLocation> = [];

    for (const location of roundedLocations) {
      const lastItem = polyline[polyline.length - 1];
      if (distanceBetween(lastItem, location) > 1000) {
        polyline.length && result.push([...polyline]);
        polyline.splice(0);
        polyline.push(location);
      } else {
        polyline.push(location);
      }
    }

    result.push(polyline);

    return result;
  }, [roundedLocations]);

  useEffect(() => {
    if (!isLoaded || showMapChildren) {
      return;
    }
    setTimeout(() => setShowMapChildren(true), 1000);
  }, [isLoaded, showMapChildren]);

  const centerMap = useCallback(
    (newMarkers: Array<LatLng>) => {
      try {
        const bounds = new google.maps.LatLngBounds();
        if (!map) {
          return;
        }

        for (const { lat, lng } of newMarkers) {
          bounds.extend(new google.maps.LatLng(lat, lng));
        }

        map.fitBounds(bounds, 0);
      } catch (err) {
        console.error("render marker error", err);
      }
    },
    [map]
  );

  useEffect(() => {
    if (!isLoaded || !(latestLocations.length || locations.length)) {
      return;
    }
    centerMap(locations.length ? locations : latestLocations);
  }, [isLoaded, latestLocations, locations, centerMap]);

  const onLoad = useCallback((map: google.maps.Map) => {
    const bounds = new window.google.maps.LatLngBounds(center[0]);
    bounds.extend(new window.google.maps.LatLng(center[1].lat, center[1].lng));
    map.fitBounds(bounds);

    setMap(map);
  }, []);

  const onUnmount = useCallback(() => setMap(null), []);

  const handleBusMarkerClick = (busAttendanceId: string) => {
    const newSelectedBus = busAttendances.find(
      (i) => i.busAttendanceId === busAttendanceId
    );

    dispatch(
      setSelectedBus(
        (busAttendanceId !== selectedBus?.busAttendanceId && newSelectedBus) ||
          null
      )
    );
  };

  return (
    <div className="w-100 h-100 p-4 d-flex gap-3">
      <Col xxl={4} lg={4} md={4} sm={4} xs={24}>
        <Controls />
        <SelectedLocationDetails />
        <SelectedBusDetails />
      </Col>
      <Col xxl={8} lg={8} md={8} sm={8} xs={24}>
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "calc(100vh - 48px)" }}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              mapTypeControlOptions: { mapTypeIds: [] },
              streetViewControl: false,
            }}
          >
            {showMapChildren && (
              <>
                {selectedBusAttendance ? (
                  <>
                    {polylines.map((polyline, index) => (
                      <Fragment key={index}>
                        {polyline.length === 1 ? (
                          <BusMarker
                            key={polyline[0].id}
                            position={polyline[0]}
                            onClick={() =>
                              handleBusMarkerClick(polyline[0].busAttendanceId)
                            }
                            isSelected={
                              selectedBus?.busAttendanceId ===
                              polyline[0].busAttendanceId
                            }
                          />
                        ) : (
                          <CustomPolyline path={polyline} />
                        )}
                      </Fragment>
                    ))}
                    {roundedLocations.map((l, i) =>
                      showPickedUp && l.children.length ? (
                        <PickedUpChildrenMarker
                          key={i}
                          position={l}
                          onClick={() => dispatch(setSelectedLocation(l))}
                          isSelected={selectedLocation?.index === i}
                        />
                      ) : (
                        showBusStops &&
                        l.isStop && (
                          <BusStopMarker
                            key={i}
                            stopPoint={l}
                            onClick={() => dispatch(setSelectedLocation(l))}
                            isSelected={selectedLocation?.index === i}
                          />
                        )
                      )
                    )}
                  </>
                ) : (
                  latestLocations.map((l) => (
                    <BusMarker
                      key={l.id}
                      position={l}
                      onClick={() => handleBusMarkerClick(l.busAttendanceId)}
                      isSelected={
                        selectedBus?.busAttendanceId === l.busAttendanceId
                      }
                    />
                  ))
                )}
              </>
            )}
          </GoogleMap>
        )}
      </Col>
    </div>
  );
};

export default LocationTracking;
